import { InputButton } from '@abbott-components/core';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { JSONSchema7 } from 'json-schema';
import { useSnackbar } from 'notistack';
import { useContext, useState, useEffect } from 'react';
import { put } from '../Shared/ajax';
import ControlState from '../Shared/controlState';
import { getErrorMessage } from '../Shared/errorHandling';
import {
  WorkflowEngineActionKind,
  WorkflowEngineContext
} from '../WorkflowEngine/workflowEngineReducer';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { FormHelperText } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Divider from '@mui/material/Divider';

interface UpdatePageRequest {
  externalId: string;
  metadata: string;
}

interface UpdatePageResponse {
  externalId: string;
}

interface PageOption {
  $id: string;
  default: string;
  type: string;
}

export default (props: { control: ControlState; onClose: () => void }) => {
  const { control, onClose } = props;
  const { state: workflowEngineState, dispatch } = useContext(
    WorkflowEngineContext
  );
  const { enqueueSnackbar } = useSnackbar();
  const { pageId } = control;

  const [selectOptions, setSelectOptions] = useState<PageOption[]>([]);
  const [selectedPage, setSelectedPage] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(true);

  const savePagesSchemas = async (
    sourcePageId: string,
    sourcePage: JSONSchema7,
    targetPageId: string,
    targetPage: JSONSchema7
  ) => {
    try {
      await put<UpdatePageResponse, UpdatePageRequest>('DynamicWorkflowPage', {
        externalId: sourcePageId,
        metadata: JSON.stringify(sourcePage)
      });

      await put<UpdatePageResponse, UpdatePageRequest>('DynamicWorkflowPage', {
        externalId: targetPageId,
        metadata: JSON.stringify(targetPage)
      });

      enqueueSnackbar('Control was successfully moved!', {
        variant: 'success'
      });
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  };

  const getOptionPages = () => {
    const validPages = workflowEngineState.pages
      .filter((page) => page.id !== pageId)
      .map((page) => page.schema.properties?.page as JSONSchema7);

    const options = validPages.map((page) => {
      if (page.properties) {
        return page.properties.label as PageOption;
      }
    }) as PageOption[];

    setSelectOptions(options);
  };

  const moveComponent = () => {
    if (selectedPage.length === 0) {
      setIsValid(false);
      return;
    }

    const targetPageId = selectedPage.split('/')[0].substring(1);

    const sourcePage = workflowEngineState.pages.find(
      (page) => page.id == pageId
    );

    const targetPage = workflowEngineState.pages.find(
      (page) => page.id == targetPageId
    );

    //Move control to other page
    dispatch({
      type: WorkflowEngineActionKind.CONTROL_MOVE,
      payload: {
        control: control,
        targetPageId: targetPageId
      }
    });

    savePagesSchemas(
      pageId,
      sourcePage?.schema as JSONSchema7,
      targetPageId,
      targetPage?.schema as JSONSchema7
    );
  };

  const handleSelectPage = (event: SelectChangeEvent) => {
    setSelectedPage(event.target.value as string);
    setIsValid(true);
  };

  useEffect(() => {
    getOptionPages();
  }, []);

  return (
    <Stack sx={{ minWidth: '250px' }}>
      <Stack direction="column" spacing={2} justifyContent="center">
        <Typography variant="h5" align="center">
          Move Component
        </Typography>
        <Divider />
        <Typography>Move to...</Typography>
        <FormControl fullWidth size="small" error={!isValid}>
          <InputLabel id="select-page-to-move">Select page ...</InputLabel>
          <Select
            id="select-page-to-move"
            value={selectedPage}
            label="Select page ..."
            onChange={handleSelectPage}
          >
            {selectOptions.map((opt) => (
              <MenuItem value={opt.$id} key={opt.$id}>
                <div
                  title={opt.default}
                  style={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  <em>{opt.default}</em>
                </div>
              </MenuItem>
            ))}
          </Select>
          {!isValid ? <FormHelperText>Select a page</FormHelperText> : null}
        </FormControl>
      </Stack>
      <Stack direction="row" mt={2} gap={1} justifyContent="right">
        <InputButton
          name="cancelDelete"
          type="button"
          variant="text"
          onClick={onClose}
        >
          Cancel
        </InputButton>
        <InputButton name="confirmDelete" type="button" onClick={moveComponent}>
          Submit
        </InputButton>
      </Stack>
    </Stack>
  );
};
